import styled, { css } from 'styled-components';

import type { PrefixProperties } from '@tmh-website/shared/types';
import ButtonIcon from '@tmh-website/ui/ButtonIcon';
import { Logo, LogoCharging, LogoEnergy, LogoSolutions } from '@tmh-website/components/assets';

import type { HeaderThemeColor } from './Header.theme';
import AudienceTabs from './AudienceTabs/AudienceTabs';
import { headerThemes } from './Header.theme';
import { StyledNavigationLink } from './NavigationLink';

export const AdjustedAudienceTabs = styled(AudienceTabs)`
  @media (${props => props.theme.breakpoint.md.down}) {
    display: none !important;
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  width: 100%;
  max-width: ${props => props.theme.siteMaxWidthLarge};
  height: 100%;
  min-height: ${props => props.theme.headerNavigationHeightMobile};
  padding: 0 ${props => props.theme.wrapperPaddingMobile};
  margin: 0 auto;
  @media (${props => props.theme.breakpoint.xs.up}) {
    gap: 20px;
  }
  @media (${props => props.theme.breakpoint.sm.up}) {
    gap: 30px;
  }
  @media (${props => props.theme.breakpoint.md.up}) {
    gap: 40px;
    min-height: ${props => props.theme.headerNavigationHeight};
    padding: 0 ${props => props.theme.wrapperPadding};
  }
`;

export type LogoWrapperProps = PrefixProperties<
  {
    height: number;
  },
  '$'
>;

export const LogoWrapper = styled.a<LogoWrapperProps>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: ${({ $height }) => `${$height || 40}px`};
  padding: 10px;
  margin: -10px;
  overflow: hidden;
  text-align: left;
`;

export interface LogoSVGProps {
  height?: number;
  width?: number;
}

const LogoSVGStyles = css<LogoSVGProps>`
  width: 160px;
  height: auto;
  @media (${props => props.theme.breakpoint.xs.up}) {
    width: 170px;
  }
  @media (${props => props.theme.breakpoint.sm.up}) {
    width: 200px;
  }
`;

export const LogoSVG = styled(Logo)<LogoSVGProps>`
  ${LogoSVGStyles};
`;

export const LogoSVGCharging = styled(LogoCharging)<LogoSVGProps>`
  ${LogoSVGStyles};
`;

export const LogoSVGEnergy = styled(LogoEnergy)<LogoSVGProps>`
  ${LogoSVGStyles};
`;

export const LogoSVGSolutions = styled(LogoSolutions)<LogoSVGProps>`
  ${LogoSVGStyles};
`;

export const CustomLogo = styled.img`
  display: inline-block;
  height: 100%;
  object-fit: contain;
  object-position: left center;
`;

export interface HeaderProps {
  $themeColor: HeaderThemeColor;
  $isHidden: boolean;
  $isTransparent: boolean;
  $localeSwitchBannerIsVisible: boolean;
}

export const GlobalHeader = styled.header<HeaderProps>`
  position: fixed;
  top: ${props => (props.$localeSwitchBannerIsVisible ? props.theme.localeSwitchBannerHeightSmallMobile : 0)};
  left: 0;
  z-index: 200;
  width: 100%;
  min-height: ${props => props.theme.headerNavigationHeightMobile};
  background-color: ${props => props.theme.color[headerThemes[props.$themeColor].background]};
  box-shadow: 0 -10px 0 ${props => props.theme.color[headerThemes[props.$themeColor].background]};
  transition: transform ${props => props.theme.transitionTiming} ${props => (props.$isHidden ? '0.03s' : '')},
    background-color 0.5s ease;
  transform: translateY(${props => (props.$isHidden ? '-100%' : 0)});
  @media (${props => props.theme.breakpoint.sm.up}) {
    top: ${props => (props.$localeSwitchBannerIsVisible ? props.theme.localeSwitchBannerHeight : 0)};
  }
  @media (${props => props.theme.breakpoint.md.up}) {
    min-height: ${props => props.theme.headerNavigationHeight};
  }

  html.is-route-change & {
    // Disable transition on route change
    transition: none !important;
  }

  ${LogoWrapper}, ${StyledNavigationLink.ClickArea} {
    color: ${props => props.theme.color[headerThemes[props.$themeColor].text]};
  }
`;

export const RightContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.gap.xSmall};
  align-items: center;
  margin-left: auto;
`;

export const HideOnMobile = styled.div`
  @media (${props => props.theme.breakpoint.md.down}) {
    display: none;
  }
`;

export const BackToTopBtn = styled(ButtonIcon)<{ $isVisible: boolean }>`
  position: fixed;
  right: ${props => props.theme.gap.xSmall};
  bottom: ${props => props.theme.gap.xSmall};
  z-index: 99;
  padding: 16px;
  border-radius: ${({ theme }) => (theme.isBorderRadiusDisabled ? 0 : '99999px')};
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transform: translateY(${props => (props.$isVisible ? 0 : '8px')});
  @media (${props => props.theme.breakpoint.md.up}) {
    display: none !important;
  }
`;
